import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Search,
  Bell,
  HelpCircle,
  RefreshCw,
  Check,
  CircleAlert,
  ChevronDown,
  LogOut,
  Shield,
  Settings as SettingsIcon,
  Sparkles,
  Eye,
  BrainCircuit,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { trades, strategies } from "@/lib/mockData";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const ROUTE_TITLES = {
  "/app": { title: "Übersicht", slug: "overview" },
  "/app/journal": { title: "Trading Journal", slug: "journal" },
  "/app/strategien": { title: "Strategien", slug: "strategien" },
  "/app/analyse": { title: "Analyse", slug: "analyse" },
  "/app/ki-coach": { title: "KI-Coach", slug: "coach" },
  "/app/steuer": { title: "Steuer-Tool", slug: "steuer" },
  "/app/einstellungen": { title: "Einstellungen", slug: "einstellungen" },
};

const SYNC_STATES = {
  synced: { label: "Synchronisiert", Icon: Check, tone: "success" },
  syncing: { label: "Wird synchronisiert", Icon: RefreshCw, tone: "info" },
  offline: { label: "Offline gespeichert", Icon: CircleAlert, tone: "warning" },
  required: { label: "Synchronisation erforderlich", Icon: CircleAlert, tone: "warning" },
};

const toneClass = {
  success: "text-[hsl(122_39%_65%)]",
  info: "text-[hsl(210_25%_72%)]",
  warning: "text-[hsl(32_60%_70%)]",
};

const notifications = [
  { id: "n1", title: "Preisabweichung bei DAX-Trade #4213", when: "vor 4 Std.", type: "Konflikt" },
  { id: "n2", title: "12 Broker-Trades synchronisiert", when: "heute, 09:12", type: "Sync" },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_META = {
  orientierung: { label: "Orientierung", hint: "Ich baue mein Fundament auf" },
  struktur:     { label: "Struktur",     hint: "Ich strukturiere meine Erfahrung" },
  optimierung:  { label: "Optimierung",  hint: "Ich optimiere gezielt meine Performance" },
};

export default function Topbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout, refresh, simMode, simStage, enterSim, exitSim } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [syncKey, setSyncKey] = useState("synced");
  const [stageBusy, setStageBusy] = useState(false);

  const page = ROUTE_TITLES[location.pathname] ?? { title: "Bereich", slug: "area" };
  const sync = SYNC_STATES[syncKey];

  const displayName = user && user !== false ? (user.name || user.email?.split("@")[0]) : "Gast";
  const email = user && user !== false ? user.email : "";
  const initials = displayName ? displayName.split(/[.\s@]/).filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase() : "?";
  const stage = user?.onboarding?.stage;
  const stageMeta = stage ? STAGE_META[stage] : null;

  const changeStage = async (next) => {
    if (!next || next === stage || stageBusy) return;
    setStageBusy(true);
    try {
      await axios.patch(`${API}/auth/me/stage`, { stage: next }, { withCredentials: true });
      if (refresh) await refresh();
      toast.success(`Nutzergruppe: ${STAGE_META[next].label}`, {
        description: "Dein Coach passt sich ab jetzt an — der KI-Coach kann dir jederzeit auch einen Wechsel vorschlagen.",
      });
    } catch (e) {
      toast.error("Wechsel fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally { setStageBusy(false); }
  };

  const handleSignout = async () => {
    try {
      await logout();
      toast.success("Du wurdest abgemeldet.");
      navigate("/anmelden");
    } catch (e) {
      toast.error("Abmelden fehlgeschlagen.");
    }
  };

  // Cmd/Ctrl + K to open search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const goTo = (path) => {
    setSearchOpen(false);
    navigate(path);
  };

  const triggerSync = () => {
    if (syncKey === "syncing") return;
    setSyncKey("syncing");
    setTimeout(() => setSyncKey("synced"), 1200);
  };

  return (
    <>
      <header
        className="hidden lg:flex sticky top-0 z-30 items-center gap-4 px-6 md:px-10 h-14 border-b border-border bg-background/85 backdrop-blur-xl"
        data-testid="topbar"
      >
        <h1
          className="font-heading text-sm text-foreground/90 truncate min-w-0"
          data-testid="topbar-title"
        >
          {page.title}
        </h1>

        <div className="flex-1" />

        <button
          onClick={() => setSearchOpen(true)}
          className="hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-full border border-border bg-secondary/60 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          data-testid="topbar-search-trigger"
          aria-label="Globale Suche öffnen"
        >
          <Search className="h-3.5 w-3.5" strokeWidth={1.5} />
          <span>Suchen…</span>
          <span className="ml-2 rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground/80">
            ⌘K
          </span>
        </button>

        <button
          onClick={triggerSync}
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border text-xs ${toneClass[sync.tone]} hover:bg-secondary/60 transition-colors`}
          data-testid="topbar-sync-status"
          title="Synchronisation aktualisieren"
        >
          <sync.Icon
            className={`h-3.5 w-3.5 ${syncKey === "syncing" ? "animate-spin" : ""}`}
            strokeWidth={1.5}
          />
          <span>{sync.label}</span>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full relative"
              data-testid="topbar-notifications"
              aria-label="Benachrichtigungen"
            >
              <Bell className="h-4 w-4" strokeWidth={1.5} />
              {notifications.length > 0 && (
                <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-[hsl(32_60%_65%)]" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
              Benachrichtigungen
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifications.map((n) => (
              <DropdownMenuItem
                key={n.id}
                className="flex-col items-start gap-1 py-3"
                onSelect={() => goTo("/app/journal")}
                data-testid={`notification-${n.id}`}
              >
                <div className="text-sm text-foreground">{n.title}</div>
                <div className="text-xs text-muted-foreground">
                  {n.when} · {n.type}
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-xs text-muted-foreground justify-center"
              onSelect={() => toast.info("Alle Benachrichtigungen als gelesen markiert")}
            >
              Alle als gelesen markieren
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full"
          onClick={() => window.dispatchEvent(new CustomEvent("trademind:open-support"))}
          data-testid="topbar-help"
          aria-label="Hilfe & Support"
          title="Hilfe & Support"
        >
          <HelpCircle className="h-4 w-4" strokeWidth={1.5} />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="inline-flex items-center gap-2 h-9 pl-1 pr-3 rounded-full border border-border hover:bg-secondary/60 transition-colors"
              data-testid="topbar-profile"
              aria-label="Profilmenü"
            >
              <span className="h-7 w-7 rounded-full bg-primary/25 border border-primary/40 grid place-items-center text-[11px] font-heading text-primary-foreground">
                {initials}
              </span>
              <span className="text-sm font-medium text-foreground/90 max-w-[8rem] truncate" data-testid="topbar-user-name">
                {displayName}
              </span>
              {stageMeta && (
                <span
                  className="hidden md:inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary border border-border text-muted-foreground"
                  data-testid="topbar-stage-badge"
                  title={stageMeta.hint}
                >
                  <Sparkles className="h-3 w-3" strokeWidth={1.75} /> {stageMeta.label}
                </span>
              )}
              {isAdmin && (
                <span
                  data-testid="topbar-admin-badge"
                  className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/20 border border-primary/40 text-primary-foreground"
                >
                  <Shield className="h-3 w-3" strokeWidth={1.75} /> Admin
                </span>
              )}
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="text-sm flex items-center gap-2">
                {displayName}
                {isAdmin && (
                  <span
                    data-testid="profile-admin-badge"
                    className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/20 border border-primary/40 text-primary-foreground"
                  >
                    Admin
                  </span>
                )}
              </span>
              <span className="text-xs text-muted-foreground font-normal truncate" data-testid="profile-email">{email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground pb-0">
              Nutzergruppe
            </DropdownMenuLabel>
            {Object.entries(STAGE_META).map(([slug, meta]) => {
              const active = stage === slug;
              return (
                <DropdownMenuItem
                  key={slug}
                  onSelect={(e) => { e.preventDefault(); changeStage(slug); }}
                  disabled={stageBusy || active}
                  className="flex-col items-start gap-0.5 py-2"
                  data-testid={`profile-stage-${slug}`}
                >
                  <div className="flex items-center gap-2 w-full">
                    <span className={`text-sm ${active ? "text-foreground font-medium" : "text-foreground/90"}`}>
                      {meta.label}
                    </span>
                    {active && <Check className="h-3.5 w-3.5 text-primary ml-auto" strokeWidth={2} />}
                  </div>
                  <span className="text-[11px] text-muted-foreground">{meta.hint}</span>
                </DropdownMenuItem>
              );
            })}
            <div className="px-2 py-1.5 text-[10px] text-muted-foreground border-t border-border mt-1">
              Dein Coach kann dir bei Bedarf einen Wechsel vorschlagen — die manuelle Auswahl hier hat Vorrang.
            </div>
            <DropdownMenuSeparator />
            {isAdmin && (
              <>
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground pb-0">
                  Nutzer-Simulation
                </DropdownMenuLabel>
                {Object.entries(STAGE_META).map(([slug, meta]) => {
                  const active = simMode && simStage === slug;
                  return (
                    <DropdownMenuItem
                      key={`sim-${slug}`}
                      onSelect={(e) => {
                        e.preventDefault();
                        enterSim(slug);
                        toast.success(`Simulation: ${meta.label}`, {
                          description: slug === "orientierung"
                            ? "Quiz-Banner, Onboarding und Product-Tour werden angezeigt wie bei frischer Registrierung."
                            : `Du siehst die App aus Sicht von Nutzer:innen der Gruppe „${meta.label}".`,
                        });
                        navigate("/app");
                      }}
                      className="flex items-center gap-2"
                      data-testid={`profile-menu-sim-${slug}`}
                    >
                      <Eye className="h-4 w-4" strokeWidth={1.5} />
                      <span className="flex-1">{meta.label}</span>
                      {active && <Check className="h-3.5 w-3.5 text-primary" strokeWidth={2} />}
                    </DropdownMenuItem>
                  );
                })}
                {simMode && (
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); exitSim(); toast.info("Simulation beendet"); }}
                    className="flex items-center gap-2 text-muted-foreground"
                    data-testid="profile-menu-sim-exit"
                  >
                    <X className="h-4 w-4" strokeWidth={1.5} /> Simulation beenden
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    if (!simMode) enterSim("orientierung");
                    toast.success("Quiz-Simulation", {
                      description: "Ergebnisse werden nur lokal berechnet — dein echtes Wissensprofil bleibt unangetastet.",
                    });
                    navigate("/app/quiz");
                  }}
                  className="flex items-center gap-2"
                  data-testid="profile-menu-sim-quiz"
                >
                  <BrainCircuit className="h-4 w-4" strokeWidth={1.5} />
                  <span className="flex-1">Quiz simulieren</span>
                </DropdownMenuItem>
                <div className="px-2 py-1.5 text-[10px] text-muted-foreground border-t border-border mt-1">
                  Rein clientseitig — deine echten Daten bleiben unberührt.
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem asChild data-testid="profile-menu-settings">
              <Link to="/app/einstellungen" className="flex items-center gap-2">
                <SettingsIcon className="h-4 w-4" strokeWidth={1.5} /> Einstellungen
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => navigate("/app/einstellungen")}
              className="flex items-center gap-2"
            >
              <Shield className="h-4 w-4" strokeWidth={1.5} /> Datenschutz
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={handleSignout}
              className="flex items-center gap-2 text-[hsl(0_66%_72%)]"
              data-testid="profile-menu-signout"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} /> Abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogTitle className="sr-only">Globale Suche</DialogTitle>
        <DialogDescription className="sr-only">
          Nach Trades, Strategien und Bereichen suchen. Mit den Pfeiltasten navigieren und mit Enter bestätigen.
        </DialogDescription>
        <CommandInput placeholder="Suche in Trades, Strategien, Bereichen…" data-testid="command-search-input" />
        <CommandList>
          <CommandEmpty>Nichts gefunden.</CommandEmpty>
          <CommandGroup heading="Bereiche">
            {Object.entries(ROUTE_TITLES).map(([path, r]) => (
              <CommandItem
                key={path}
                onSelect={() => goTo(path)}
                data-testid={`command-area-${r.slug}`}
              >
                {r.title}
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Trades">
            {trades.slice(0, 5).map((t) => (
              <CommandItem
                key={t.id}
                onSelect={() => goTo("/app/journal")}
                data-testid={`command-trade-${t.id}`}
              >
                <span className="font-mono text-xs text-muted-foreground mr-2">{t.id}</span>
                <span>
                  {t.name} · {t.side}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Strategien">
            {strategies.map((s) => (
              <CommandItem
                key={s.id}
                onSelect={() => goTo("/app/strategien")}
                data-testid={`command-strategy-${s.id}`}
              >
                {s.name}
                <span className="ml-2 text-xs text-muted-foreground">v{s.version}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
